import { useMemo } from 'react'
import {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  OnChangeFn,
  VisibilityState,
} from '@tanstack/table-core'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { Link, useParams } from 'react-router-dom'
import { Anchor, NumberFormatter, Text } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { tableDefaultAlertBannerProps, tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { sortLotNumbers } from '@shared/helpers/sort-lot-numbers/sort-lot-numbers'
import { LotDetail, WoolSoldStatus } from '@loom-api-types'
import { lotDetailsColumnLabels } from './lot-details-labels'

export const enum ResultOptions {
  Sold = 'Sold',
  Unsold = 'Unsold',
}

export const columnsForRemovalInReducedView = [
  'purchaseAccountName',
  'type',
  'comment',
  'code',
  'cleanKgs',
  'coreTestCoefficientOfVariationLaserscan',
  'weighDate',
  'lottedDate',
  'qualityScheme2',
  'recordType',
  'sample',
  'saleAccount',
  'paymentDate',
]

export const financialColumns = ['result', 'grossProceeds', 'priceClean', 'priceGreasy']

interface LotDetailsTableProps {
  data: LotDetail[]
  sumOfTotalBales?: number
  sumOfTotalNetKg?: number
  weightedAverageOfMicron?: number
  weightedAverageOfVegetableMatter?: number
  weightedAverageOfYield?: number
  weightedAverageOfColour?: number
  weightedAverageOfStapleLength?: number
  weightedAverageOfStapleStrength?: number
  weightedAverageOfPriceGreasy?: number
  weightedAverageOfPriceClean?: number
  weightedAverageOfCoefficientOfVariationLaserscan?: number
  weightedAverageOfStapleAverageStrength?: number
  weightedAverageOfPointOfBreakTip?: number
  weightedAverageOfPointOfBreakMid?: number
  weightedAverageOfPointOfBreakBase?: number
  sumOfTotalGrossProceeds?: number
  weightedAverageOfLaserscanMicron?: number
  weightedAverageOfOfdaMicron?: number
  weightedAverageOfAirflowMicron?: number
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  columnFilters?: ColumnFiltersState
  setColumnFilters?: OnChangeFn<ColumnFiltersState>
  enableResultFilter?: boolean
  reducedView?: boolean
  hideFinancialColumns?: boolean
}

export const LotDetailsTable = ({
  data = [],
  sumOfTotalBales,
  sumOfTotalNetKg,
  weightedAverageOfMicron,
  weightedAverageOfVegetableMatter,
  weightedAverageOfYield,
  weightedAverageOfColour,
  weightedAverageOfStapleLength,
  weightedAverageOfStapleStrength,
  weightedAverageOfPriceGreasy,
  weightedAverageOfPriceClean,
  weightedAverageOfCoefficientOfVariationLaserscan,
  weightedAverageOfStapleAverageStrength,
  weightedAverageOfPointOfBreakTip,
  weightedAverageOfPointOfBreakMid,
  weightedAverageOfPointOfBreakBase,
  sumOfTotalGrossProceeds,
  weightedAverageOfLaserscanMicron,
  weightedAverageOfOfdaMicron,
  weightedAverageOfAirflowMicron,
  noDataMessage = 'No lot details available for this station',
  isLoading,
  isError,
  columnFilters,
  setColumnFilters,
  enableResultFilter = true,
  reducedView = false,
  hideFinancialColumns = false,
}: LotDetailsTableProps) => {
  const { stationRecordNumber } = useParams()

  const columns = useMemo<MRT_ColumnDef<LotDetail>[]>(() => {
    const allColumns: MRT_ColumnDef<LotDetail>[] = [
      {
        id: 'referenceNumber',
        accessorKey: 'referenceNumber',
        header: lotDetailsColumnLabels.referenceNumber,
        enableHiding: false,
        enableSorting: true,
        sortingFn: sortLotNumbers,
        enableColumnFilter: false,
        Footer: () => <>Totals & AV</>,
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: lotDetailsColumnLabels.description,
        enableHiding: false,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: 'bales',
        accessorKey: 'bales',
        header: lotDetailsColumnLabels.bales,
        Cell: ({ cell, row }) => {
          const searchParams = new URLSearchParams()
          if (row.original.referenceNumber && row.original.season) {
            searchParams.append('referenceNumber', row.original.referenceNumber)
            searchParams.append('season', row.original.season)
            return (
              <Anchor
                pl='lg'
                component={Link}
                to={`/stations/${stationRecordNumber}/wool-data/bale-details?${searchParams.toString()}`}
                size='sm'
              >
                {cell.getValue<number>()}
              </Anchor>
            )
          }
          return cell.getValue<number>()
        },
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableHiding: false,
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>TOTAL</Text>
            <NumberFormatter value={sumOfTotalBales ?? ''} thousandSeparator />
          </>
        ),
      },
      {
        id: 'netKgs',
        accessorKey: 'netKgs',
        header: lotDetailsColumnLabels.netKgs,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} thousandSeparator />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableHiding: false,
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>TOTAL</Text>
            <NumberFormatter value={sumOfTotalNetKg ?? ''} thousandSeparator />
          </>
        ),
      },
      {
        id: 'lineNumber',
        accessorKey: 'lineNumber',
        header: lotDetailsColumnLabels.lineNumber,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: 'saleAndSeason',
        accessorKey: 'saleAndSeason',
        header: lotDetailsColumnLabels.saleAndSeason,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'select',
      },
      {
        id: 'purchaseAccountName',
        accessorKey: 'purchaseAccountName',
        header: lotDetailsColumnLabels.purchaseAccountName,
        enableSorting: false,
        enableColumnFilter: true,
        filterVariant: 'select',
      },
      {
        id: 'coreTestMicron',
        accessorKey: 'coreTestMicron',
        header: lotDetailsColumnLabels.coreTestMicron,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={1}
          />
        ),
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfMicron ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'coreTestVegetableMatterBase',
        accessorKey: 'coreTestVegetableMatterBase',
        header: lotDetailsColumnLabels.coreTestVegetableMatterBase,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={1}
          />
        ),
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfVegetableMatter ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'coreTestYield',
        accessorKey: 'coreTestYield',
        header: lotDetailsColumnLabels.coreTestYield,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={1}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfYield ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'coreTestColourYZ',
        accessorKey: 'coreTestColourYZ',
        header: lotDetailsColumnLabels.coreTestColourYZ,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={1}
          />
        ),
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfColour ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'coreTestStapleLengthGreasy',
        accessorKey: 'coreTestStapleLengthGreasy',
        header: lotDetailsColumnLabels.coreTestStapleLengthGreasy,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={0}
          />
        ),
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfStapleLength ?? ''}
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'coreTestStapleStrength',
        accessorKey: 'coreTestStapleStrength',
        header: lotDetailsColumnLabels.coreTestStapleStrength,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={0}
          />
        ),
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfStapleStrength ?? ''}
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: lotDetailsColumnLabels.type,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: 'comment',
        accessorKey: 'comment',
        header: lotDetailsColumnLabels.comment,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'result',
        accessorKey: 'result',
        header: lotDetailsColumnLabels.result,
        enableSorting: false,
        enableColumnFilter: enableResultFilter,
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: [ResultOptions.Sold, ResultOptions.Unsold],
        },
        Cell: ({ cell }) =>
          cell.getValue<string>() === WoolSoldStatus.SoldOrBin ? ResultOptions.Sold : '',
      },
      {
        id: 'priceGreasy',
        accessorKey: 'priceGreasy',
        header: lotDetailsColumnLabels.priceGreasy,
        Cell: ({ cell }) => (
          <NumberFormatter
            suffix='c'
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={0}
          />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfPriceGreasy ?? ''}
              suffix='c'
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'priceClean',
        accessorKey: 'priceClean',
        header: lotDetailsColumnLabels.priceClean,
        Cell: ({ cell }) => (
          <NumberFormatter
            suffix='c'
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={0}
          />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfPriceClean ?? ''}
              suffix='c'
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'code',
        accessorKey: 'code',
        header: lotDetailsColumnLabels.code,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: sortLotNumbers,
      },
      {
        id: 'cleanKgs',
        accessorKey: 'cleanKgs',
        header: lotDetailsColumnLabels.cleanKgs,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={0}
          />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'coreTestCoefficientOfVariationLaserscan',
        accessorKey: 'coreTestCoefficientOfVariationLaserscan',
        header: lotDetailsColumnLabels.coreTestCoefficientOfVariationLaserscan,
        Cell: ({ cell }) => (
          <NumberFormatter
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={1}
          />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfCoefficientOfVariationLaserscan ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'coreTestStapleAverageStrength',
        accessorKey: 'coreTestStapleAverageStrength',
        header: lotDetailsColumnLabels.coreTestStapleAverageStrength,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} decimalScale={0} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: false,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfStapleAverageStrength ?? ''}
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'coreTestPointOfBreakTip',
        accessorKey: 'coreTestPointOfBreakTip',
        header: lotDetailsColumnLabels.coreTestPointOfBreakTip,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} decimalScale={0} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: false,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfPointOfBreakTip ?? ''}
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'coreTestPointOfBreakMid',
        accessorKey: 'coreTestPointOfBreakMid',
        header: lotDetailsColumnLabels.coreTestPointOfBreakMid,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} decimalScale={0} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: false,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfPointOfBreakMid ?? ''}
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'coreTestPointOfBreakBase',
        accessorKey: 'coreTestPointOfBreakBase',
        header: lotDetailsColumnLabels.coreTestPointOfBreakBase,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} decimalScale={0} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: false,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfPointOfBreakBase ?? ''}
              thousandSeparator
              decimalScale={0}
            />
          </>
        ),
      },
      {
        id: 'grossProceeds',
        accessorKey: 'grossProceeds',
        header: lotDetailsColumnLabels.grossProceeds,
        Cell: ({ cell }) => (
          <NumberFormatter
            prefix='$'
            value={cell.getValue<number>() ?? ''}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
          />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>TOTAL</Text>
            <NumberFormatter
              prefix='$'
              value={sumOfTotalGrossProceeds ?? ''}
              thousandSeparator
              decimalScale={2}
              fixedDecimalScale
            />
          </>
        ),
      },
      {
        id: 'paymentDate',
        header: lotDetailsColumnLabels.paymentDate,
        accessorFn: (row) => (row.paymentDate == null ? null : new Date(row.paymentDate)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: 'coreTestMicronLaserscan',
        accessorKey: 'coreTestMicronLaserscan',
        header: lotDetailsColumnLabels.coreTestMicronLaserscan,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} decimalScale={1} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfLaserscanMicron ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'coreTestMicronOfda',
        accessorKey: 'coreTestMicronOfda',
        header: lotDetailsColumnLabels.coreTestMicronOfda,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} decimalScale={1} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfOfdaMicron ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'coreTestMicronAirflow',
        accessorKey: 'coreTestMicronAirflow',
        header: lotDetailsColumnLabels.coreTestMicronAirflow,
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>() ?? ''} decimalScale={1} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableSorting: true,
        enableColumnFilter: false,
        Footer: () => (
          <>
            <Text size='sm'>AV</Text>
            <NumberFormatter
              value={weightedAverageOfAirflowMicron ?? ''}
              thousandSeparator
              decimalScale={1}
            />
          </>
        ),
      },
      {
        id: 'weighDate',
        header: lotDetailsColumnLabels.weighDate,
        accessorFn: (row) => (row.weighDate == null ? null : new Date(row.weighDate)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: 'lottedDate',
        header: lotDetailsColumnLabels.lottedDate,
        accessorFn: (row) => (row.lottedDate == null ? null : new Date(row.lottedDate)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: 'qualityScheme2',
        accessorKey: 'qualityScheme2',
        header: lotDetailsColumnLabels.qualityScheme2,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'recordType',
        accessorKey: 'recordType',
        header: lotDetailsColumnLabels.recordType,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'sample',
        accessorKey: 'sample',
        header: lotDetailsColumnLabels.sample,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'saleAccount',
        accessorKey: 'saleAccount',
        header: lotDetailsColumnLabels.saleAccount,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ]

    let filteredColumns = allColumns
    if (reducedView) {
      filteredColumns = allColumns.filter(
        (column) => column.id !== undefined && !columnsForRemovalInReducedView.includes(column.id)
      )
    }
    if (hideFinancialColumns) {
      filteredColumns = filteredColumns.filter(
        (column) => column.id !== undefined && !financialColumns.includes(column.id)
      )
    }

    return filteredColumns
  }, [
    enableResultFilter,
    sumOfTotalBales,
    sumOfTotalGrossProceeds,
    sumOfTotalNetKg,
    weightedAverageOfAirflowMicron,
    weightedAverageOfCoefficientOfVariationLaserscan,
    weightedAverageOfColour,
    weightedAverageOfLaserscanMicron,
    weightedAverageOfMicron,
    weightedAverageOfOfdaMicron,
    weightedAverageOfPointOfBreakBase,
    weightedAverageOfPointOfBreakMid,
    weightedAverageOfPointOfBreakTip,
    weightedAverageOfPriceClean,
    weightedAverageOfPriceGreasy,
    weightedAverageOfStapleAverageStrength,
    weightedAverageOfStapleLength,
    weightedAverageOfStapleStrength,
    weightedAverageOfVegetableMatter,
    weightedAverageOfYield,
    reducedView,
    hideFinancialColumns,
    stationRecordNumber,
  ])

  // Determine initial column settings, depending if reduced view (Loom vs Grower Gateway) or not
  // so we avoid leaking columns names that are not relevant to the user in  storage.
  const initialColumnOrder = useMemo(() => {
    let columns = Object.keys(lotDetailsColumnLabels)
    if (reducedView) {
      columns = columns.filter((col) => !columnsForRemovalInReducedView.includes(col))
    }
    return columns
  }, [reducedView])

  const initialColumnVisibility: VisibilityState = {
    code: false,
    cleanKgs: false,
    coreTestCoefficientOfVariationLaserscan: false,
    coreTestStapleAverageStrength: false,
    coreTestPointOfBreakTip: false,
    coreTestPointOfBreakMid: false,
    coreTestPointOfBreakBase: false,
    paymentDate: false,
    coreTestMicronLaserscan: false,
    coreTestMicronOfda: false,
    coreTestMicronAirflow: false,
    weighDate: false,
    lottedDate: false,
    qualityScheme2: false,
    recordType: false,
    sample: false,
    saleAccount: false,
  }

  // Local storage for column settings
  const [columnOrder, setColumnOrder] = useLocalStorage<ColumnOrderState>({
    key: 'nzm.lotDetailsTableColumnOrder',
    defaultValue: initialColumnOrder,
  })
  const [columnPinning, setColumnPinning] = useLocalStorage<ColumnPinningState>({
    key: 'nzm.lotDetailsTableColumnPinning',
    defaultValue: { left: ['referenceNumber'], right: [] },
  })
  const [columnVisibility, setColumnVisibility] = useLocalStorage<VisibilityState>({
    key: 'nzm.lotDetailsTableColumnVisibility',
    defaultValue: initialColumnVisibility,
  })

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    initialState: {
      ...tableDefaultProps.initialState,
      showColumnFilters: true,
    },
    state: {
      isLoading,
      showAlertBanner: isError,
      columnFilters,
      columnPinning,
      columnOrder,
      columnVisibility,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Lot Details',
    },
    // Sorting
    enableSorting: true,
    sortingFns: {
      sortLotNumbers,
    },
    // Visibility
    enableHiding: true,
    onColumnVisibilityChange: setColumnVisibility,
    // Column filters
    enableFilters: true,
    manualFiltering: true,
    enableFacetedValues: true,
    onColumnFiltersChange: setColumnFilters,
    enableFilterMatchHighlighting: false,
    // Column order, allow reordering but not via header handles to save space
    enableColumnOrdering: true,
    enableColumnDragging: false,
    onColumnOrderChange: setColumnOrder,
    // Column pinning
    enableColumnPinning: true,
    onColumnPinningChange: setColumnPinning,
  })

  const renderKey = `lot-details-table-${reducedView ? 'reduced' : 'full'}-${hideFinancialColumns ? 'hide-financials' : 'show-financials'}`

  return <MantineReactTable table={table} key={renderKey} />
}
